import { useState } from "react";
import { useNavigate } from "react-router";

import AuthRoutes from "Routes/Components/Auth";

import moment from "Helpers/MomentCompat";

import UseAuth from "Hooks/UseAuth";

import Button from "Components/Global/Buttons";
import Select from "Components/Global/Form/Select";
import GetIcon from "Components/Global/Icons";

import BookButton from "../BookButton";
import Filters from "../Filters";
import Project from "../Project";
import * as S from "./styles";

import { useTheme } from "styled-components";

const AllProjects = ({
  // A aba "Seletivas" não passa `projects` (ela renderiza `selectives`
  // agrupadas) — o default evita depender de qual ramo do JSX é avaliado.
  projects = [],
  selectives,
  isSelective,
  selectedFilter,
  setSelectedFilter,
  selected,
  setSelected,
  projectFilters,
  setProjectFilters,
  // Aba "Projetos Aportados" do proponente (18/08/2026): ela reusa esta lista,
  // mas não é o lugar de criar projeto nem de dizer "nenhum projeto cadastrado".
  hideCreateButton = false,
  emptyMessage = "Nenhum projeto cadastrado.",
}) => {
  const { parsedJWT } = UseAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  // Seletivas "Finalizada" (is_history) ficam num bloco recolhido: continuam
  // acessíveis, mas o foco é sempre a seletiva vigente ("Enviado"). Seletivas em
  // "Em montagem" nem chegam aqui — o backend não as devolve.
  const [historyOpen, setHistoryOpen] = useState(false);

  const currentSelectives = (selectives || []).filter(
    (selective) => !selective.is_history,
  );
  const historySelectives = (selectives || []).filter(
    (selective) => selective.is_history,
  );

  const renderProjectCard = (project) => {
    const realization_address = project.realization_address
      ? Object.keys(JSON.parse(project.realization_address)).join(", ")
      : "N/A";

    return (
      <S.Project
        key={project.id}
        onClick={() => {
          setSelected(project);
        }}
      >
        {project.project_image ||
        (project.project_images && project.project_images[0]) ? (
          <img
            src={project.project_image || project.project_images[0]}
            alt="Imagem do projeto"
          />
        ) : (
          <S.EmptyBg />
        )}

        <S.ProjectInfos>
          <h1>
            {project.project_name?.length > 30
              ? project.project_name.slice(0, 30) + "..."
              : project.project_name}
          </h1>

          {project.resource_finish_at && (
            <S.Row>
              <span>Prazo de captação</span>
              <span>
                {moment(project.resource_finish_at).format("DD/MM/YYYY")}
              </span>
            </S.Row>
          )}

          <S.Row>
            <span>Status</span>
            <span>{project.status}</span>
          </S.Row>

          <S.Row>
            <span>Lei de Incentivo</span>
            <span>
              {project.project_modality?.length > 13
                ? project.project_modality.slice(0, 13) + "..."
                : project.project_modality}
            </span>
          </S.Row>

          <S.Row>
            <span>UF</span>
            <span>
              {realization_address?.length > 13
                ? realization_address.slice(0, 13) + "..."
                : realization_address}
            </span>
          </S.Row>

          <Button
            variant="tertiary"
            text={is_sponsor ? "Abrir" : "Editar"}
            type="button"
            onClick={() => {
              setSelected(project);
            }}
          />

          {/* Book de captação do projeto. O campo já chega como URL assinada
              (validade de 1h) no payload da listagem, então não há chamada nova
              aqui. Em "Validado" o payload é o simplificado e não traz o campo:
              o botão não aparece, que é o comportamento correto para esse
              status. Sem book anexado, também não aparece. */}
          {project.caption_book_src && (
            <BookButton
              variant="tertiary"
              outline
              text="PDF comercial"
              type="button"
              onClick={(event) => {
                // O card inteiro tem onClick que abre o detalhe do projeto —
                // sem isto o PDF abriria E o detalhe apareceria por baixo.
                event.stopPropagation();
                window.open(
                  project.caption_book_src,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            />
          )}
        </S.ProjectInfos>
      </S.Project>
    );
  };

  const renderSelectiveGroup = (selective) => (
    <S.SelectiveGroup key={selective.id}>
      <S.SelectiveHeader>
        <h2>{selective.name}</h2>

        <S.SelectiveActions>
          {selective.start_at && selective.finish_at && (
            <span>
              {moment(selective.start_at).format("DD/MM/YYYY")} –{" "}
              {moment(selective.finish_at).format("DD/MM/YYYY")}
            </span>
          )}

          {/* Sem anexo o backend devolve presentation_src nulo e nada aparece. */}
          {selective.presentation_src && (
            <a
              href={selective.presentation_src}
              download
              target="_blank"
              rel="noreferrer"
            >
              <S.DownloadPresentation>
                <h1>Baixar apresentação</h1>
                <GetIcon name="Download" color={theme.colors.neutral.white} />
              </S.DownloadPresentation>
            </a>
          )}
        </S.SelectiveActions>
      </S.SelectiveHeader>

      <S.Projects>
        {selective.projects?.length ? (
          selective.projects.map(renderProjectCard)
        ) : (
          <span>Nenhum projeto nesta seletiva.</span>
        )}
      </S.Projects>
    </S.SelectiveGroup>
  );

  return selected ? (
    <Project
      id={selected?.id}
      setSelected={setSelected}
      isSelective={isSelective}
    />
  ) : (
    <>
      {!is_sponsor && !hideCreateButton ? (
        <S.Header>
          <Button
            text="Adicionar novo projeto"
            type="button"
            onClick={() => {
              navigate(AuthRoutes.CreateProject.path);
            }}
            icon={{
              name: "Plus",
              position: "left",
              color: theme.colors.neutral.white,
            }}
          />
        </S.Header>
      ) : !isSelective && setProjectFilters ? (
        // Filtro da carteira de aportados. Só do patrocinador: a aba "Meus
        // Projetos" do proponente segue sem filtro.
        <Filters filters={projectFilters} setFilters={setProjectFilters} />
      ) : isSelective ? (
        <S.Header>
          <Select
            name="filter"
            options={[
              { value: null, label: "Todos" },
              { value: "selected", label: "Selecionados" },
              { value: "not_selected", label: "Não Selecionados" },
              { value: "in_evaluation", label: "Em avaliação" },
            ]}
            value={selectedFilter}
            onChange={(option) => {
              setSelectedFilter(option);
            }}
          />
        </S.Header>
      ) : null}

      {isSelective ? (
        <S.Selectives>
          {currentSelectives.length === 0 && (
            <span>Nenhuma seletiva encontrada.</span>
          )}

          {currentSelectives.map(renderSelectiveGroup)}

          {historySelectives.length > 0 && (
            <S.HistoryBlock>
              <S.HistoryToggle
                type="button"
                onClick={() => setHistoryOpen((open) => !open)}
                aria-expanded={historyOpen}
              >
                Histórico ({historySelectives.length})
              </S.HistoryToggle>

              {historyOpen && historySelectives.map(renderSelectiveGroup)}
            </S.HistoryBlock>
          )}
        </S.Selectives>
      ) : (
        <S.Projects>
          {projects.length === 0 && <span>{emptyMessage}</span>}

          {projects.map(renderProjectCard)}
        </S.Projects>
      )}
    </>
  );
};

export default AllProjects;
